import { useMemo } from "react";
import { Sparkline } from "./Sparkline";
import { ymdDaysAgo, type RangeWindow } from "../../lib/range";
import type { ActivityActionType, TimeseriesResponse } from "../../lib/api/types";

interface KpiStripProps {
  stats: TimeseriesResponse | undefined;
  window: RangeWindow;
  activeInvestors: number;
  now?: number;
}

/** Sum a metric across investors for one bucket. */
function bucketSum(
  bucket: TimeseriesResponse["buckets"][number],
  metric: ActivityActionType | "all",
): number {
  let n = 0;
  for (const v of Object.values(bucket.values)) {
    if (metric === "all") n += v.opens + v.downloads;
    else if (metric === "open") n += v.opens;
    else n += v.downloads;
  }
  return n;
}

interface TileData {
  label: string;
  value: number;
  delta: number | null;
  deltaLabel: string;
  spark: number[];
  color: string;
}

export function KpiStrip({ stats, window, activeInvestors, now = Date.now() }: KpiStripProps) {
  const tiles = useMemo<TileData[]>(() => {
    const buckets = stats?.buckets ?? [];
    const splitYmd = window.currentFromYmd;
    const hasPrev = window.days != null && splitYmd != null;

    const current = hasPrev ? buckets.filter((b) => b.date >= splitYmd) : buckets;
    const previous = hasPrev ? buckets.filter((b) => b.date < splitYmd) : [];

    const sum = (bs: typeof buckets, m: ActivityActionType | "all") =>
      bs.reduce((acc, b) => acc + bucketSum(b, m), 0);

    const opensCur = sum(current, "open");
    const downloadsCur = sum(current, "download");
    const opensPrev = sum(previous, "open");
    const downloadsPrev = sum(previous, "download");

    // Active investors: distinct firms with any event in the trailing 7d window.
    const last7 = ymdDaysAgo(7, now);
    const prev7 = ymdDaysAgo(14, now);
    const distinctActive = (bs: typeof buckets) => {
      const ids = new Set<string>();
      for (const b of bs) {
        for (const [id, v] of Object.entries(b.values)) {
          if (v.opens + v.downloads > 0) ids.add(id);
        }
      }
      return ids.size;
    };
    const activeNow = distinctActive(buckets.filter((b) => b.date >= last7));
    const activePrev = distinctActive(buckets.filter((b) => b.date >= prev7 && b.date < last7));

    const rangeLabel = window.days != null ? `vs prev ${window.days}d` : "all time";

    return [
      {
        label: "Total opens",
        value: opensCur,
        delta: hasPrev ? opensCur - opensPrev : null,
        deltaLabel: rangeLabel,
        spark: current.map((b) => bucketSum(b, "open")),
        color: "#2563eb",
      },
      {
        label: "Total downloads",
        value: downloadsCur,
        delta: hasPrev ? downloadsCur - downloadsPrev : null,
        deltaLabel: rangeLabel,
        spark: current.map((b) => bucketSum(b, "download")),
        color: "#047857",
      },
      {
        label: "Active investors (7d)",
        value: activeInvestors,
        delta: hasPrev ? activeNow - activePrev : null,
        deltaLabel: "vs prev 7d",
        spark: current.map((b) => bucketSum(b, "all")),
        color: "#2563eb",
      },
    ];
  }, [stats, window, activeInvestors, now]);

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {tiles.map((t) => (
        <div key={t.label} className="flex flex-col gap-2 rounded-lg border border-surface-border bg-surface p-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-meta text-text-muted">{t.label}</div>
              <div className="mt-1 text-title font-semibold text-text">{t.value}</div>
            </div>
            <Sparkline data={t.spark} color={t.color} />
          </div>
          <Delta delta={t.delta} label={t.deltaLabel} />
        </div>
      ))}
    </div>
  );
}

function Delta({ delta, label }: { delta: number | null; label: string }) {
  if (delta == null) {
    return <div className="text-meta text-text-muted">{label}</div>;
  }
  // PRD §7.4: up = green, down/flat = neutral.
  const up = delta > 0;
  const arrow = delta > 0 ? "▲" : delta < 0 ? "▼" : "·";
  const sign = delta > 0 ? "+" : "";
  return (
    <div className={["text-meta", up ? "text-success-700" : "text-text-muted"].join(" ")}>
      {arrow} {sign}
      {delta} <span className="text-text-muted">{label}</span>
    </div>
  );
}
